import { Box, Typography } from '@mui/material';
import React from 'react';

// Import types from local pricing types
import type { MarketplaceService } from '../../types/pricing';
import {
  findVariant,
  getVariantList,
  usesVariantPricing,
} from '../../utils/variantHelpers';

interface SimplePricingDisplayProps {
  service: MarketplaceService;
  className?: string;
}

/**
 * Simple pricing display for marketplace cards - shows only default pricing, no selection UI
 */
export const SimplePricingDisplay: React.FC<SimplePricingDisplayProps> = ({
  service,
  className,
}) => {
  // PR-9 tile rail: when the service row opts into per-unit pricing the
  // hydrated card mirrors the SSR rail ('From $39.95 per line') instead
  // of the per-config range/total. Matches ServiceGridSSR's priority
  // logic so SSR and hydrated DOM agree. Added here because
  // ServiceCardNew -> SimplePricingDisplay is the component tree Next.js
  // re-renders on hydration; the SSR HTML was correct, but hydration
  // replaced the rail with the legacy range until now.
  const tileUnitBasis = (service as any).tile_unit_basis as string | undefined;
  const tilePriceCents = (service as any).tile_price_cents as number | null | undefined;
  const tileUnitLabel = (service as any).tile_unit_label as string | null | undefined;
  if (
    tileUnitBasis &&
    tileUnitBasis !== 'flat' &&
    typeof tilePriceCents === 'number' &&
    tileUnitLabel
  ) {
    const dollars = (tilePriceCents / 100).toFixed(2);
    return (
      <Box className={className}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}
        >
          From ${dollars} {tileUnitLabel}
        </Typography>
      </Box>
    );
  }

  const getRangeFallback = (): { price: string; suffix: string } | null => {
    const min = service.price_min_cents ?? service.price_range_min;
    const max = service.price_max_cents ?? service.price_range_max;

    const minValue = min != null ? Number(min) : null;
    const maxValue = max != null ? Number(max) : null;

    if (minValue != null && Number.isFinite(minValue) && minValue > 0) {
      if (maxValue != null && Number.isFinite(maxValue) && maxValue > minValue) {
        return {
          price: `${(minValue / 100).toFixed(2)} - ${(maxValue / 100).toFixed(2)}`,
          suffix: '/mo',
        };
      }
      return {
        price: (minValue / 100).toFixed(2),
        suffix: '/mo',
      };
    }

    const baseValue = service.base_price != null ? Number(service.base_price) : null;
    if (baseValue != null && Number.isFinite(baseValue) && baseValue > 0) {
      return {
        price: (baseValue / 100).toFixed(2),
        suffix: '/mo',
      };
    }

    return null;
  };

  const getPricingDisplay = () => {
    let price = '0.00';
    let suffix = '';
    let additionalFees: string[] = [];

    if (usesVariantPricing(service) && service.default_variant_key) {
      // Find the default variant (safe against malformed `variants` shapes —
      // some services set pricing_model='variants' but store a non-standard
      // inner payload; `findVariant` returns undefined in that case so we
      // fall through to the range/base_price fallback below).
      const defaultVariant = findVariant(service, service.default_variant_key);
      if (defaultVariant) {
        // Variants come in two shapes from the API:
        //   1. { key, pricing: { recurring_cents, frequency, ... } }                     (Foresight, gamma)
        //   2. { key, frequency, attribute1, attribute2, amount_cents }                  (AT&T, Spectrum SBPP, Comcast)
        // Either is valid — never assume `defaultVariant.pricing` exists.
        let pricing: any = defaultVariant.pricing;
        const hasContractTerms =
          service.contract_term_months && service.contract_term_months.length > 0;
        const defaultTerm =
          service.default_term_months ||
          (hasContractTerms ? service.contract_term_months[0] : null);

        if (hasContractTerms && defaultTerm && defaultVariant.term_overrides) {
          // Use term-specific pricing for contract services
          const termOverride = defaultVariant.term_overrides.find(
            (override: any) => override.term_months === defaultTerm,
          );
          if (termOverride) {
            pricing = termOverride;
          }
        }

        // Shape-2 fallback: synthesize a `pricing`-like object from the flat variant fields
        // so the rest of this block can stay shape-1-shaped.
        if (!pricing) {
          const flat: any = defaultVariant as any;
          pricing = {
            recurring_cents: flat.amount_cents ?? 0,
            frequency: flat.frequency,
          };
        }

        // Handle ranges in variant pricing
        if (
          pricing?.min_recurring_cents !== undefined &&
          pricing?.max_recurring_cents !== undefined
        ) {
          const minPrice = (pricing.min_recurring_cents / 100).toFixed(2);
          const maxPrice = (pricing.max_recurring_cents / 100).toFixed(2);
          price = `${minPrice} - ${maxPrice}`;
        } else {
          const recurringCents = Number(pricing?.recurring_cents || 0);
          if (recurringCents > 0) {
            price = (recurringCents / 100).toFixed(2);
          } else {
            const fallback = getRangeFallback();
            if (fallback) {
              price = fallback.price;
              suffix = fallback.suffix;
            }
          }
        }

        suffix = suffix ||
          pricing?.frequency === 'monthly'
            ? '/mo'
            : pricing?.frequency === 'quarterly'
              ? '/quarter'
              : pricing?.frequency === 'annually'
                ? '/year'
                : pricing?.frequency === 'one_time'
                  ? ' one time charge'
                  : '';

        // Collect additional fees (shape-1 only — shape-2 doesn't have these)
        if (pricing?.nrc_cents > 0) {
          additionalFees.push(`Setup: $${(pricing.nrc_cents / 100).toFixed(2)}`);
        }
        if (pricing?.shipping_cents > 0) {
          additionalFees.push(`Shipping: $${(pricing.shipping_cents / 100).toFixed(2)}`);
        }
        if (pricing?.other_cents > 0) {
          additionalFees.push(`Other: $${(pricing.other_cents / 100).toFixed(2)}`);
        }
      }
    } else if (
      service.pricing_model === 'multi_frequency' &&
      service.frequencies &&
      service.frequencies.length > 0
    ) {
      // Show the first (usually monthly) frequency
      const freq = service.frequencies[0];
      price = (freq.recurring_cents / 100).toFixed(2);
      suffix =
        freq.frequency === 'monthly'
          ? '/mo'
          : freq.frequency === 'quarterly'
            ? '/quarter'
            : freq.frequency === 'annually'
              ? '/year'
              : freq.frequency === 'one_time'
                ? ' one time charge'
                : '';

      // Collect additional fees
      if (freq.nrc_cents > 0) {
        additionalFees.push(`Setup: $${(freq.nrc_cents / 100).toFixed(2)}`);
      }
      if (freq.shipping_cents > 0) {
        additionalFees.push(`Shipping: $${(freq.shipping_cents / 100).toFixed(2)}`);
      }
      if (freq.other_cents > 0) {
        additionalFees.push(`Other: $${(freq.other_cents / 100).toFixed(2)}`);
      }
    } else if (service.default_pricing) {
      // Handle varying pricing (ranges)
      if (
        service.default_pricing.min_recurring_cents !== undefined &&
        service.default_pricing.max_recurring_cents !== undefined
      ) {
        const minPrice = (service.default_pricing.min_recurring_cents / 100).toFixed(2);
        const maxPrice = (service.default_pricing.max_recurring_cents / 100).toFixed(2);
        price = `${minPrice} - ${maxPrice}`;
        suffix =
          service.default_pricing.frequency === 'monthly'
            ? '/mo'
            : service.default_pricing.frequency === 'quarterly'
              ? '/quarter'
              : service.default_pricing.frequency === 'annually'
                ? '/year'
                : service.default_pricing.frequency === 'one_time'
                  ? ' one time charge'
                  : '';

        // Collect additional fees
        if (service.default_pricing.nrc_cents > 0) {
          additionalFees.push(`Setup: $${(service.default_pricing.nrc_cents / 100).toFixed(2)}`);
        }
        if (service.default_pricing.shipping_cents > 0) {
          additionalFees.push(
            `Shipping: $${(service.default_pricing.shipping_cents / 100).toFixed(2)}`,
          );
        }
        if (service.default_pricing.other_cents > 0) {
          additionalFees.push(`Other: $${(service.default_pricing.other_cents / 100).toFixed(2)}`);
        }
      } else {
        // Fixed pricing
        const recurringCents = Number(service.default_pricing.recurring_cents || 0);
        if (recurringCents > 0) {
          price = (recurringCents / 100).toFixed(2);
        } else {
          const fallback = getRangeFallback();
          if (fallback) {
            price = fallback.price;
            suffix = fallback.suffix;
          }
        }
        suffix = suffix ||
          service.default_pricing.frequency === 'monthly'
            ? '/mo'
            : service.default_pricing.frequency === 'quarterly'
              ? '/quarter'
              : service.default_pricing.frequency === 'annually'
                ? '/year'
                : service.default_pricing.frequency === 'one_time'
                  ? ' one time charge'
                  : '';

        // Collect additional fees
        if (service.default_pricing.nrc_cents > 0) {
          additionalFees.push(`Setup: $${(service.default_pricing.nrc_cents / 100).toFixed(2)}`);
        }
        if (service.default_pricing.shipping_cents > 0) {
          additionalFees.push(
            `Shipping: $${(service.default_pricing.shipping_cents / 100).toFixed(2)}`,
          );
        }
        if (service.default_pricing.other_cents > 0) {
          additionalFees.push(`Other: $${(service.default_pricing.other_cents / 100).toFixed(2)}`);
        }
      }
    } else if (service.base_price !== undefined && service.base_price !== null) {
      const fallback = getRangeFallback();
      if (fallback) {
        price = fallback.price;
        suffix = fallback.suffix;
      }
    }

    return { price, suffix, additionalFees };
  };

  const pricing = getPricingDisplay();
  let prefix = '';
  if (usesVariantPricing(service)) {
    const variantList = getVariantList(service);
    if (variantList.length > 1) {
      const prices = variantList
        .map((v: any) => v?.pricing?.recurring_cents)
        .filter((n: any) => typeof n === 'number');
      const unique = new Set(prices);
      if (unique.size > 1) prefix = 'From ';
    }
  }

  return (
    <Box className={className}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 'bold',
          color: 'text.primary',
          mb: 1,
        }}
      >
        {prefix}${pricing.price}{pricing.suffix}
      </Typography>

      {/* Show additional fees if any exist */}
      {pricing.additionalFees.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {pricing.additionalFees.map((fee, index) => (
            <Typography
              key={index}
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.7rem', display: 'block' }}
            >
              {fee}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};
